import { snakeCase } from "lodash";
import { ModelType } from "~/enums/modelType";
import IRepo from "~/interfaces/repo";
import { DatetimeMode } from "~/models/datetimeMode";
import { RelationType } from "~/models/relationType";
import VisualizationSource from "~/models/visualizations/visualizationSource";
import VisualizationSourceChangedIndexedFields from "~/models/visualizations/visualizationSourceChangedIndexedFields";
import Repo from "~/state/modules/repo";
import Form from "../../../formability/form_builder/models/form";
import FormElement from "../../../formability/form_builder/models/formElements/formElement";
import { FormElementType } from "../../enums/formElementType";
import { InputFormatType } from "../../../formability/form_builder/models/inputFormatType";
import VisualizationSourceDataPoint from "./visualizationSourceDataPoint";
import {DataPointType} from "~/enums/visualizationType";

export default class VisualizationSourceForm extends VisualizationSource {
  dimensionTypes = [FormElementType.dropdown, FormElementType.radioButtonGroup, FormElementType.checkboxGroup, FormElementType.dateTimePicker, FormElementType.textField]
  measureTypes = [FormElementType.textField]
  numericTypes = [InputFormatType.Numeric, InputFormatType.Currency]
  _builtinDimensions: VisualizationSourceDataPoint[];
  _builtinMeasures: VisualizationSourceDataPoint[];
  _formDimensions: VisualizationSourceDataPoint[];
  _formMeasures: VisualizationSourceDataPoint[];
  changed_indexed_fields: VisualizationSourceChangedIndexedFields;
  source_workflow_step_completers: any;
  source_all_workflow_steps: any;
  source_assign_to_completers: any;

  readonly friendly_type = ModelType.Form;

  source_client_id: string;

  constructor() {
    super();
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'changed_indexed_fields': { type: VisualizationSourceChangedIndexedFields, relationType: RelationType.singleObject },
    };
    return models;
  }

  createEmbeddedObject = {
    changed_indexed_fields: (parent, json) => {
      let obj: VisualizationSourceChangedIndexedFields;
      obj = new VisualizationSourceChangedIndexedFields(json)
      if (obj) obj._nameInParent = 'changed_indexed_fields';
      obj.addToRepo()
      return obj;
    },
  }

  static repo: IRepo<VisualizationSourceForm> = new Repo<VisualizationSourceForm>('visualizationSourceForm', VisualizationSourceForm, {});
  repo: IRepo<this>;

  addToRepo(): void {
    VisualizationSourceForm.repo.add(this);
  }

  get builtinDimensions(): VisualizationSourceDataPoint[] {
    if (this._builtinDimensions) return this._builtinDimensions;

    this._builtinDimensions = [
      "Assigned To",
      "Workflow Step Completer",
      "All Workflow Steps",
      "All Statuses",
      "Submitted On",
      "Completed On",
      "Step Started On",
      "Step Completed On"
    ].map(title => new VisualizationSourceDataPoint({title, referenced_client_id: snakeCase("static_" + title), data_point_type: DataPointType.Dimension, source: this.source}, this.parent))
    return this._builtinDimensions
  }

  get workflowStepCompleters() {
    return this.source_workflow_step_completers
  }

  get workflowStepTitles() {
    return this.source_all_workflow_steps
  }

  get assignToCompleters() {
    return this.source_assign_to_completers
  }

  get builtinMeasures(): VisualizationSourceDataPoint[] {
    if (this._builtinMeasures) return this._builtinMeasures;

    this._builtinMeasures = [
      "Number of Submissions",
      "Step Duration",
    ].map(title => new VisualizationSourceDataPoint({title: title, referenced_client_id: snakeCase("static_" + title), data_point_type: DataPointType.Measure, source: this.source}, this.parent))

    return this._builtinMeasures
  }

  get formMeasures(): VisualizationSourceDataPoint[] {
    if (this._formMeasures) return this._formMeasures;

    this._formMeasures = this.source.indexedElements
      .filter(e => e.isTypeOneOf(this.measureTypes))
      .filter(e => this.numericTypes.includes(e.format_type))
      .map(e => new VisualizationSourceDataPoint({title: e.displayTitle, referenced_client_id: e.client_id, data_point_type: DataPointType.Measure, source: this.source}, this.parent))

    return this._formMeasures;
  }

  get formDimensions(): VisualizationSourceDataPoint[] {
    if (this._formDimensions) return this._formDimensions;

    this._formDimensions = this.source.indexedElements
      .filter(e => e.isTypeOneOf(this.dimensionTypes))
      .filter(e => {
        if (e.element_type === FormElementType.dateTimePicker) {
          return e.datetimeMode === DatetimeMode.Date || e.datetimeMode === DatetimeMode.Datetime;
        }

        if (e.element_type === FormElementType.textField) {
          return !this.numericTypes.includes(e.format_type);
        }

        return true;
      })
      .map(e => new VisualizationSourceDataPoint({title: e.displayTitle, referenced_client_id: e.client_id, data_point_type: DataPointType.Dimension, source: this.source}, this.parent))

    return this._formDimensions;
  }

  get source() {
    let f = Form.repo.find(this.source_client_id);
    if (!f) {
      f = Form.repo.findOrCreate(this.source_obj_as_json_for_builder.id, this.source_obj_as_json_for_builder.client_id).loadFromJson(this.source_obj_as_json_for_builder);
    }
    return f;
  }

  // built-in and indexed picklist, dropdown, checkbox, radio & datetime fields/properties
  get dimensions(): VisualizationSourceDataPoint[] {
    return this.builtinDimensions.concat(this.formDimensions);
  }

  // built-in and indexed numeric fields/properties
  get measures(): VisualizationSourceDataPoint[] {
    return this.builtinMeasures.concat(this.formMeasures)
  }

  get allMeasuresAndDimensions(): VisualizationSourceDataPoint[] {
    return this.formDimensions.concat(this.formMeasures);
  }

  getIndexedElement(clientId: string): FormElement | null {
    return this.source.indexedElements.find(e => e.client_id === clientId);
  }

  get newlyIndexedFields(): FormElement[] {
    return this.source.indexedElements.filter(el => {
      return this.changed_indexed_fields.added_fields.includes(el.client_id)
    })
  }

  get newlyUnindexedFields(): FormElement[] {
    return this.source.getFormElements.filter(el => {
      return this.changed_indexed_fields.removed_fields.includes(el.client_id)
    })
  }

  get removedIndexedFieldsLabels(): string[] {
    let unindexedElementsLabels = this.newlyUnindexedFields.map(element => element.label_title)
    let deletedIndexedElementsLabels = this.changed_indexed_fields.removed_fields.filter(field => {
      return !field.match(/^[a-zA-Z0-9\-]{36}$/) // filter out client_ids and leave only labels
    })
    return unindexedElementsLabels.concat(deletedIndexedElementsLabels)
  }

  clearChangedFields(): void {
    this.changed_indexed_fields.clearChangedFields()
  }
}
